import { useQuery } from '@apollo/client';
import { Grid, TextField, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Configuration,
  CONFIGURATION_TYPE,
  getIssueTypes,
  getWorkOrderStatuses,
  getWorkOrdersWithFilters,
  MIXPANEL_EVENTS,
  WORK_ORDER_CREATION_STATUS,
  WorkOrder,
  WorkOrderStatus,
} from 'vendor-marketplace-shared-library';

import IssueTypesSelector from '../../components/common/IssueTypesSelector';
import PropertySelector from '../../components/common/PropertySelector';
import SecondaryButton from '../../components/common/styled-mui-overrides/SecondaryButton';
import WorkOrderAssigneeSelector from '../../components/work-orders/WorkOrderAssigneeSelector';
import WorkOrderPrioritySelector from '../../components/work-orders/WorkOrderPrioritySelector';
import WorkOrderCard from '../../components/work-orders/WorkOrderCard';
import useDebounce from '../../hooks/useDebounce';
import useMixpanel from '../../hooks/useMixpanel';
import CreateWorkOrderAction from '../../components/work-orders/CreateWorkOrderAction';
import Loader from '../../components/common/Loader';
import { getConfigurationValue } from '../../utilities/helpers';
import useConfigurations from '../../hooks/useConfigurations';

export default function HomePage() {
  const mixpanel = useMixpanel();
  const navigate = useNavigate();
  const { configurationsQuery } = useConfigurations();
  const [search, setSearch] = useState('');
  const debouncedSearchText = useDebounce(search, 300);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [issueTypeIds, setIssueTypeIds] = useState<string[]>([]);
  const [priorityIds, setPriorityIds] = useState<string[]>([]);
  const [assigneeUserIds, setAssigneeUserIds] = useState<string[]>([]);

  const { data } = useQuery(getWorkOrdersWithFilters, {
    variables: {
      creationStatus: WORK_ORDER_CREATION_STATUS.COMPLETE,
      searchText: debouncedSearchText,
      propertyIds,
      issueTypeIds,
      workOrderPriorityIds: priorityIds,
      assigneeUserIds,
    },
  });

  const { data: workOrderStatusesData } = useQuery(getWorkOrderStatuses);
  const { data: issueTypeData, loading } = useQuery(getIssueTypes);
  const issueTypes = issueTypeData?.getIssueTypes || [];
  const showIssueTypes = issueTypes.length > 1;

  const columns = ['Open', 'In Progress', 'Done'];
  const sortStatuses = (a: WorkOrderStatus, b: WorkOrderStatus) =>
    columns.indexOf(a.name) - columns.indexOf(b.name);

  const resetAllFilters = () => {
    setSearch('');
    setPropertyIds([]);
    setIssueTypeIds([]);
    setPriorityIds([]);
    setAssigneeUserIds([]);
  };

  const setFilter = (
    value: string[],
    filterName: string,
    setter: Dispatch<SetStateAction<string[]>>,
  ) => {
    mixpanel.mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_DASHBOARD_SEARCH_AND_FILTER, {
      filter: filterName,
    });
    setter(value);
  };

  const onPropertyChange = (value: string[]) => {
    setFilter(value, 'Property Filter', setPropertyIds);
  };

  const onIssueTypeChange = (value: string[]) => {
    setFilter(value, 'Issue Type Filter', setIssueTypeIds);
  };

  const onPriorityChange = (value: string[]) => {
    setFilter(value, 'Priority Filter', setPriorityIds);
  };

  const onAssigneeChange = (value: string[]) => {
    setFilter(value, 'Assignee Filter', setAssigneeUserIds);
  };

  const onWorkOrderClick = (workOrder: WorkOrder) => {
    mixpanel.mixpanelTrack(MIXPANEL_EVENTS.WORK_ORDER_DASHBOARD_WORK_ORDER_PRESSED, {
      workOrderId: workOrder.id,
    });
    navigate(`/work-orders/${workOrder.id}`);
  };

  const userNonWarrantyWorkOrderCreationIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.NON_WARRANTY_WORK_ORDER_CREATION_IS_ENABLED,
    );

  const isUserNonWarrantyWorkOrderCreationIsEnabled = getConfigurationValue(
    userNonWarrantyWorkOrderCreationIsEnabledConfiguration,
  );

  useEffect(() => {
    mixpanel.mixpanelTrack('Home Page Viewed');
  }, []);

  const filterWidth =
    showIssueTypes && isUserNonWarrantyWorkOrderCreationIsEnabled
      ? 2
      : showIssueTypes || isUserNonWarrantyWorkOrderCreationIsEnabled
      ? 2.4
      : 3;

  if (loading) {
    return <Loader />;
  }

  return (
    <Grid container rowGap={2} sx={Styles.mainContainer}>
      <CreateWorkOrderAction />
      {/*Filters container*/}
      <Grid item container xs={12}>
        <Grid item xs={12} md={12}>
          <Typography variant={'h6'}>Filters</Typography>
        </Grid>
        <Grid item container spacing={2} alignItems={'center'} xs={12}>
          <Grid item xs={12} lg={filterWidth}>
            <TextField
              fullWidth
              label='Search'
              variant='outlined'
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} lg={filterWidth}>
            <PropertySelector value={propertyIds} onChange={onPropertyChange} />
          </Grid>
          {showIssueTypes && (
            <Grid item xs={12} lg={filterWidth}>
              <IssueTypesSelector value={issueTypeIds} onChange={onIssueTypeChange} />
            </Grid>
          )}
          {isUserNonWarrantyWorkOrderCreationIsEnabled && (
            <Grid item xs={12} lg={filterWidth}>
              <WorkOrderPrioritySelector value={priorityIds} onChange={onPriorityChange} />
            </Grid>
          )}
          <Grid item xs={12} lg={filterWidth}>
            <WorkOrderAssigneeSelector value={assigneeUserIds} onChange={onAssigneeChange} />
          </Grid>
          <Grid item xs={12} lg={filterWidth}>
            <SecondaryButton fullWidth onClick={resetAllFilters}>
              Clear
            </SecondaryButton>
          </Grid>
        </Grid>
      </Grid>
      {/*Swimlanes*/}
      <Grid item sx={Styles.swimlanesContainer}>
        <Grid container columnGap={1} sx={Styles.noWrap}>
          {[...(workOrderStatusesData?.getWorkOrderStatuses || [{ id: 1 }, { id: 2 }, { id: 4 }])]
            .sort(sortStatuses)
            .map((status: WorkOrderStatus, index) => (
              <Grid item container key={status.id} sx={Styles.swimlaneContainer}>
                <Grid item>
                  <Typography variant={'h6'}>{status.name}</Typography>
                </Grid>
                <Grid item container direction={'column'} rowGap={1} sx={Styles.swimlane}>
                  {data?.getWorkOrdersWithFilters.length > 0 ? (
                    data?.getWorkOrdersWithFilters
                      .filter((workOrder: WorkOrder) => workOrder.workOrderStatus!.id === status.id)
                      .map((workOrder: WorkOrder) => (
                        <Grid item key={workOrder.id}>
                          <WorkOrderCard
                            workOrder={workOrder}
                            onClick={() => onWorkOrderClick(workOrder)}
                          />
                        </Grid>
                      ))
                  ) : index === 0 ? (
                    <Typography>No work orders yet.</Typography>
                  ) : null}
                </Grid>
              </Grid>
            ))}
        </Grid>
      </Grid>
    </Grid>
  );
}

const Styles = {
  swimlane: {
    backgroundColor: 'secondary.light',
    paddingTop: 1,
    borderRadius: 2,
    height: '90vh',
    overflowX: 'hidden',
    overflowY: 'scroll',
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: { xs: '310px', sm: '385px' },
    '&::-webkit-scrollbar': {
      display: 'none', // Hide scrollbar for Chrome, Safari, and Opera
    },
    '-ms-overflow-style': 'none', // Hide scrollbar for IE and Edge
    'scrollbar-width': 'none', // Hide scrollbar for Firefox
  },
  swimlaneContainer: {
    width: '385px',
    minWidth: '310px',
    overflowY: 'hidden',
    flexDirection: 'column',
  },
  swimlanesContainer: {
    overflowX: 'auto',
    maxWidth: { xs: '310px', sm: '100%' },
  },
  mainContainer: {
    maxWidth: '1180px',
    justifyContent: 'center',
  },
  noWrap: {
    flexWrap: 'nowrap',
  },
};
